import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ImagePlus, X, Car, Tag, Link2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const photoUrl = (p) => `${API}/vehicle-photos/${p}`;
const FUEL_TYPES = ["Petrol", "Diesel", "Hybrid", "Plug-in Hybrid", "Electric"];

const EMPTY = {
  make: "", model: "", year: "", price: "", mileage: "",
  fuel_type: "Petrol", gearbox: "", mot_date: "", service_history: "",
  description: "", photos: [],
};

export const AdminVehicles = ({ pw }) => {
  const [vehicles, setVehicles] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | vehicle object
  const headers = { "X-Admin-Password": pw };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/vehicles`);
      setVehicles(res.data.vehicles || []);
    } catch {
      toast.error("Failed to load vehicles.");
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleStatus = async (v) => {
    const status = v.status === "sold" ? "available" : "sold";
    try {
      await axios.put(`${API}/admin/vehicles/${v.id}`, { status }, { headers });
      toast.success(status === "sold" ? "Marked as SOLD." : "Marked as available.");
      load();
    } catch {
      toast.error("Update failed.");
    }
  };

  const copyLink = async (v) => {
    const url = `${window.location.origin}/sales/${v.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Advert link copied to clipboard.");
    } catch {
      window.prompt("Copy this link:", url);
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete the ${v.make} ${v.model} listing permanently?`)) return;
    try {
      await axios.delete(`${API}/admin/vehicles/${v.id}`, { headers });
      toast.success("Listing deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  if (editing) {
    return (
      <VehicleForm
        pw={pw}
        initial={editing === "new" ? null : editing}
        onDone={(saved) => { setEditing(null); if (saved) load(); }}
      />
    );
  }

  return (
    <div data-testid="admin-vehicles">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-[var(--owrs-text-muted)]">
          Listings appear instantly on the public <span className="text-[var(--owrs-text)]">Vehicles For Sale</span> page.
        </p>
        <button onClick={() => setEditing("new")} data-testid="admin-add-vehicle-btn" className="btn-primary !py-2 !px-4 text-sm">
          <Plus className="h-4 w-4" /> Add vehicle
        </button>
      </div>

      {vehicles.length === 0 ? (
        <div className="surface p-10 text-center text-[var(--owrs-text-muted)]">
          No vehicles listed yet. Click "Add vehicle" to create your first advert.
        </div>
      ) : (
        <div className="space-y-4">
          {vehicles.map((v) => (
            <div key={v.id} className="surface p-4 sm:p-5 flex flex-wrap items-center gap-4" data-testid={`admin-vehicle-row-${v.id}`}>
              <div className="h-16 w-24 shrink-0 overflow-hidden bg-black/30 flex items-center justify-center">
                {v.photos?.length > 0 ? (
                  <img src={photoUrl(v.photos[0])} alt="" className="h-full w-full object-cover" />
                ) : (
                  <Car className="h-6 w-6 text-[var(--owrs-text-dim)]" />
                )}
              </div>
              <div className="flex-1 min-w-[180px]">
                <div className="font-display text-lg uppercase leading-tight">
                  {v.year} {v.make} {v.model}
                </div>
                <div className="text-sm text-[var(--owrs-text-muted)]">
                  £{Number(v.price).toLocaleString("en-GB")} · {Number(v.mileage).toLocaleString("en-GB")} miles · {v.fuel_type}
                  {v.photos?.length > 0 && ` · ${v.photos.length} photo${v.photos.length > 1 ? "s" : ""}`}
                </div>
              </div>
              <span className={`px-3 py-1 text-xs tracking-[0.2em] uppercase ${
                v.status === "sold"
                  ? "bg-[var(--owrs-accent)] text-[var(--owrs-accent-ink)]"
                  : "border border-[var(--owrs-border-strong)] text-[var(--owrs-text-muted)]"
              }`}>
                {v.status === "sold" ? "Sold" : "For sale"}
              </span>
              <div className="flex items-center gap-2">
                <button onClick={() => copyLink(v)} data-testid={`admin-vehicle-copylink-${v.id}`}
                  title="Copy advert link"
                  className="btn-secondary !py-1.5 !px-3 text-xs">
                  <Link2 className="h-3.5 w-3.5" /> Copy link
                </button>
                <button onClick={() => toggleStatus(v)} data-testid={`admin-vehicle-toggle-${v.id}`}
                  className="btn-secondary !py-1.5 !px-3 text-xs">
                  <Tag className="h-3.5 w-3.5" /> {v.status === "sold" ? "Relist" : "Mark sold"}
                </button>
                <button onClick={() => setEditing(v)} data-testid={`admin-vehicle-edit-${v.id}`}
                  className="btn-secondary !py-1.5 !px-3 text-xs">
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
                <button onClick={() => remove(v)} data-testid={`admin-vehicle-delete-${v.id}`}
                  className="btn-secondary !py-1.5 !px-3 text-xs hover:!border-red-400 hover:!text-red-400">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

function VehicleForm({ pw, initial, onDone }) {
  const [form, setForm] = useState(() =>
    initial
      ? {
          make: initial.make, model: initial.model, year: String(initial.year),
          price: String(initial.price), mileage: String(initial.mileage),
          fuel_type: initial.fuel_type, gearbox: initial.gearbox || "",
          mot_date: initial.mot_date || "", service_history: initial.service_history || "",
          description: initial.description, photos: initial.photos || [],
        }
      : { ...EMPTY }
  );
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const headers = { "X-Admin-Password": pw };

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const res = await axios.post(`${API}/admin/vehicles/photos`, fd, { headers });
        setForm((f) => ({ ...f, photos: [...f.photos, res.data.path] }));
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Upload failed: ${file.name}`);
      }
    }
    setUploading(false);
    e.target.value = "";
  };

  const removePhoto = (p) =>
    setForm((f) => ({ ...f, photos: f.photos.filter((x) => x !== p) }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      make: form.make.trim(), model: form.model.trim(),
      year: parseInt(form.year, 10), price: parseFloat(form.price),
      mileage: parseInt(form.mileage, 10), fuel_type: form.fuel_type,
      gearbox: form.gearbox.trim() || null, mot_date: form.mot_date.trim() || null,
      service_history: form.service_history.trim() || null,
      description: form.description.trim(), photos: form.photos,
    };
    try {
      if (initial) {
        await axios.put(`${API}/admin/vehicles/${initial.id}`, payload, { headers });
        toast.success("Listing updated.");
      } else {
        await axios.post(`${API}/admin/vehicles`, payload, { headers });
        toast.success("Vehicle listed for sale.");
      }
      onDone(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Please check all fields are filled correctly.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="surface p-6 sm:p-8" data-testid="admin-vehicle-form">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-xl sm:text-2xl uppercase">
          {initial ? "Edit listing" : "New vehicle listing"}
        </h2>
        <button type="button" onClick={() => onDone(false)} data-testid="admin-vehicle-form-cancel"
          className="text-sm text-[var(--owrs-text-dim)] hover:text-[var(--owrs-accent)]">
          Cancel
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className="owrs-label">Make</label>
          <input name="make" value={form.make} onChange={onChange} required maxLength={60}
            data-testid="vehicle-make-input" className="owrs-input" placeholder="e.g. Tesla" />
        </div>
        <div>
          <label className="owrs-label">Model</label>
          <input name="model" value={form.model} onChange={onChange} required maxLength={80}
            data-testid="vehicle-model-input" className="owrs-input" placeholder="e.g. Model 3 Long Range" />
        </div>
        <div>
          <label className="owrs-label">Year</label>
          <input name="year" type="number" min="1950" max="2035" value={form.year} onChange={onChange} required
            data-testid="vehicle-year-input" className="owrs-input" placeholder="2021" />
        </div>
        <div>
          <label className="owrs-label">Price (£)</label>
          <input name="price" type="number" min="0" step="1" value={form.price} onChange={onChange} required
            data-testid="vehicle-price-input" className="owrs-input" placeholder="24995" />
        </div>
        <div>
          <label className="owrs-label">Mileage</label>
          <input name="mileage" type="number" min="0" value={form.mileage} onChange={onChange} required
            data-testid="vehicle-mileage-input" className="owrs-input" placeholder="42000" />
        </div>
        <div>
          <label className="owrs-label">Fuel type</label>
          <select name="fuel_type" value={form.fuel_type} onChange={onChange}
            data-testid="vehicle-fuel-select" className="owrs-input">
            {FUEL_TYPES.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        <div>
          <label className="owrs-label">Gearbox (optional)</label>
          <select name="gearbox" value={form.gearbox} onChange={onChange}
            data-testid="vehicle-gearbox-select" className="owrs-input">
            <option value="">—</option>
            <option value="Automatic">Automatic</option>
            <option value="Manual">Manual</option>
          </select>
        </div>
        <div>
          <label className="owrs-label">MOT until (optional)</label>
          <input name="mot_date" value={form.mot_date} onChange={onChange} maxLength={20}
            data-testid="vehicle-mot-input" className="owrs-input" placeholder="e.g. March 2027" />
        </div>
        <div className="sm:col-span-2">
          <label className="owrs-label">Service history (optional)</label>
          <input name="service_history" value={form.service_history} onChange={onChange} maxLength={200}
            data-testid="vehicle-history-input" className="owrs-input" placeholder="e.g. Full main dealer service history" />
        </div>
        <div className="sm:col-span-2">
          <label className="owrs-label">Advert description</label>
          <textarea name="description" rows={6} value={form.description} onChange={onChange} required maxLength={6000}
            data-testid="vehicle-description-input" className="owrs-input"
            placeholder="Describe the vehicle — condition, spec, history, why it's special…" />
        </div>

        <div className="sm:col-span-2">
          <label className="owrs-label">Photos</label>
          <div className="flex flex-wrap gap-3">
            {form.photos.map((p, i) => (
              <div key={p} className="relative h-24 w-32 overflow-hidden border border-[var(--owrs-border-strong)]">
                <img src={photoUrl(p)} alt="" className="h-full w-full object-cover" />
                {i === 0 && (
                  <span className="absolute bottom-0 left-0 right-0 bg-[rgba(13,31,45,0.85)] text-[9px] tracking-[0.2em] uppercase text-center py-0.5 text-[var(--owrs-accent)]">
                    Main photo
                  </span>
                )}
                <button type="button" onClick={() => removePhoto(p)}
                  data-testid={`vehicle-photo-remove-${i}`}
                  aria-label="Remove photo"
                  className="absolute top-1 right-1 h-6 w-6 inline-flex items-center justify-center bg-[rgba(13,31,45,0.9)] border border-[var(--owrs-border-strong)] hover:text-red-400">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            <label className={`h-24 w-32 border border-dashed border-[var(--owrs-border-strong)] flex flex-col items-center justify-center gap-1.5 cursor-pointer text-[var(--owrs-text-dim)] hover:text-[var(--owrs-accent)] hover:border-[var(--owrs-accent)] transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
              <ImagePlus className="h-5 w-5" />
              <span className="text-[10px] tracking-[0.2em] uppercase">
                {uploading ? "Uploading…" : "Add photos"}
              </span>
              <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple
                onChange={uploadPhotos} className="hidden" data-testid="vehicle-photo-upload-input" />
            </label>
          </div>
          <p className="text-xs text-[var(--owrs-text-dim)] mt-2">
            The first photo is used as the advert's main image. JPG / PNG / WEBP, max 10 MB each.
          </p>
        </div>
      </div>

      <button type="submit" disabled={saving || uploading} data-testid="admin-vehicle-form-submit"
        className="btn-primary mt-8 disabled:opacity-60">
        {saving ? "Saving…" : initial ? "Save changes" : "Publish listing"}
      </button>
    </form>
  );
}
